import React from "react";

import {
  faArrowLeftLong,
  faChevronDown,
  faChevronRight,
  faChevronUp,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "lodash";
import { Link, NavLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import RetoolIcon from "src/assets/RetoolMenuIcon.svg?react";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { useEnvironment } from "src/hooks/useEnvironment";
import { UserMenu } from "src/layout/UserMenuV2";
import { useRetoolInformation } from "src/retool/Retool";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useCurrentPage } from "src/router/useCurrentPage";

type BreadcrumbProps =
  | {
      label: string;
      href: string;
      children?: never;
      dataLoc?: string;
      hideIcon?: boolean;
      active?: boolean;
    }
  | {
      children: React.ReactNode;
      dataLoc?: string;
      hideIcon?: boolean;
      label?: never;
      href?: never;
      active?: never;
    };

export type BreadcrumbType = React.FC<BreadcrumbProps>;

type Props =
  | {
      title: React.ReactNode;
      breadcrumbs?: never;
      navigation?: ArrowNavigationProps;
      sideContent?: React.ReactNode;
      backLink?: string;
    }
  | {
      title?: never;
      breadcrumbs: { key: string; breadcrumb: ReturnType<BreadcrumbType> }[];
      navigation?: ArrowNavigationProps;
      sideContent?: React.ReactNode;
      backLink?: string;
    };

type SubComponents = {
  Breadcrumb: BreadcrumbType;
};

type NavigationProps = {
  url: string;
  disabled: boolean;
};

type ArrowNavigationProps = {
  previous: NavigationProps;
  next: NavigationProps;
};

const ProfileSideContent = () => {
  const { retoolPageUrl, retoolConnection, isCurrentPageRetool } =
    useRetoolInformation();
  return (
    <div className="flex items-center gap-4">
      {!isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) &&
        retoolPageUrl && (
          <Tooltip
            placement="bottom-end"
            title={retoolConnection?.name}
            asChild
          >
            <Link
              className={
                isCurrentPageRetool ? "text-indigo-500" : "text-gray-700"
              }
              to={retoolPageUrl}
            >
              <RetoolIcon />
            </Link>
          </Tooltip>
        )}
      {!isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) && (
        <UserMenu />
      )}
    </div>
  );
};

export const PageHeader: React.FC<Props> & SubComponents = ({
  title,
  breadcrumbs,
  navigation,
  sideContent,
  backLink,
}) => {
  const [environment] = useEnvironment();
  const {
    isEntityViewPage,
    isFeaturesPage,
    isEventsPage,
    isEntitiesPage,
    isCasesPage,
  } = useCurrentPage();
  const showEnvPill =
    isEntityViewPage ||
    isFeaturesPage ||
    isEventsPage ||
    isEntitiesPage ||
    isCasesPage;

  return (
    <div
      className="sticky top-0 z-10 flex h-13 shrink-0 items-center justify-between gap-x-2 border-gray-200 border-b border-l bg-white pl-4"
      data-loc="page-header"
    >
      {backLink && (
        <NavLink data-loc="back-icon" to={backLink}>
          <Icon
            color="text-gray-500 hover:text-gray-700"
            icon={faArrowLeftLong}
            size="xs"
          />
        </NavLink>
      )}
      {title && (
        <h2 className="font-inter-semibold-13px" data-record="true">
          {title}
        </h2>
      )}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <ul
          className="flex min-w-0 max-w-full items-center gap-x-3"
          data-loc="breadcrumbs"
        >
          {breadcrumbs.map(({ key, breadcrumb }) => (
            <React.Fragment key={key}>{breadcrumb}</React.Fragment>
          ))}
        </ul>
      )}
      {navigation && <PageHeaderArrowNavigation {...navigation} />}
      {environment !== "live" && showEnvPill && (
        <Pill dataLoc="environment-pill">
          <Pill.Text>{capitalize(environment)}</Pill.Text>
        </Pill>
      )}
      <div
        className="ml-auto flex h-full items-center pr-3"
        data-loc="page-header-side-content"
      >
        {sideContent ? sideContent : <ProfileSideContent />}
      </div>
    </div>
  );
};

const PageHeaderBreadcrumb: BreadcrumbType = ({
  label,
  href,
  dataLoc,
  hideIcon,
  active,
  children,
}) => (
  <li className="flex flex-none items-center gap-x-3 truncate last:min-w-0 last:flex-1">
    {!hideIcon && (
      <Icon color="text-gray-500" icon={faChevronRight} size="xs" />
    )}
    {href ? (
      <Link
        className={twMerge(
          "truncate font-inter-semibold-13px text-indigo-600",
          active && "text-gray-800",
        )}
        data-loc={dataLoc}
        to={href}
      >
        {label}
      </Link>
    ) : (
      label
    )}
    {children}
  </li>
);

const PageHeaderArrowNavigation: React.FC<ArrowNavigationProps> = ({
  previous,
  next,
}) => {
  return (
    <div className="flex items-center gap-2">
      <Link
        className={twMerge(
          "rounded-md border border-gray-200",
          previous.disabled && "pointer-events-none opacity-50",
        )}
        to={previous.url}
      >
        <Icon color="text-gray-500" icon={faChevronDown} size="xs" />
      </Link>
      <Link
        className={twMerge(
          "rounded-md border border-gray-200",
          next.disabled && "pointer-events-none opacity-50",
        )}
        to={next.url}
      >
        <Icon color="text-gray-500" icon={faChevronUp} size="xs" />
      </Link>
    </div>
  );
};

PageHeader.Breadcrumb = PageHeaderBreadcrumb;
